// The projection reducer on this design's envelope (#159, Phase 0 unit 14).
//
// The old reducer (ported at #150, 1,534 lines) dispatched the old wire's
// names — `investigation.complete`, `timeline.tool_started`, `chat.stream` —
// and carried two payload shapes per fact, one for the live frame and one for
// the replayed row, reconciled case by case. That is the lift-table finding
// (`greenfield-design.md`, *The gross counts stand*): a new envelope and a
// post-terminal stream are not an import-path transplant. This file is the
// rewrite. One row shape, `LogRow`, which is what `read_log` returns and what
// every SSE frame's `data` carries; one function, `applyEvent`, for the replay
// and for the live tail alike; the `type` enum from the generated contract, so
// a type the envelope gains is a compile error here, not a silently dropped
// frame.
//
// Three properties (#6) pin the fold, and `investigationProjection.properties.test.ts`
// holds it to them:
//
// - live = replay over durable rows: the fold is a function of the log's rows
//   in seq order and of nothing else — not the channel they came on, not the
//   time they came, not what was pushed between them.
// - the fold of a log with ephemeral rows removed equals the fold with them:
//   `stream` lands in `state.ephemeral` and nowhere else, so the replay, which
//   never sees it, agrees with the live tab on everything the log holds. The
//   `context` is a stored row since #399 (owner, 2026-09-11) and folds into
//   `state.context`, so a reload keeps the plan the customer reads.
// - rows arriving after terminal fold the same live and on replay: `complete`
//   is the run's terminal status, not its last row (`:604-605`). Nothing here
//   closes on a terminal type; the platform's `action_*`, `consent_resolved`
//   and `outcome_observed` rows, and a chat segment's findings, fold after it
//   exactly as they would before it.
//
// What the fold keeps from the old reducer is its monotonicity — a thinner
// later row never subtracts from a richer earlier one — because that was
// measured on customers (#1502, #1509, #1510 in the old repo) and the runtime
// this repository transplanted still emits the thin `complete.findings` delta
// beside the rich `finding` rows (`agent/orchestrator.py`).
//
// **The per-type payload schema is unpinned** (`contracts/envelope.py`: "only
// `resumed` is pinned so far"). The fields read below are the ones
// `src/torquenext/agent/` records today — `record_finding.py`,
// `record_hypothesis.py`, `hooks.py`, `orchestrator.py` — read defensively:
// an absent field is an absent field, never an error, and a type whose
// payload the runtime does not emit yet (`clarification`, `request_action`,
// `tool_refused`, `request_consent`, the platform's eight) is kept whole on
// its timeline row and in the slot its lifecycle names. When the payload
// package pins a schema, the readers here narrow to it; the fold's properties
// do not depend on a field name.

import type { EventType } from "../../contracts/envelope.generated";
import type { DurableRow, EphemeralRow, LogRow } from "../../contracts/stream";

// ---------------------------------------------------------------------------
// The projection
// ---------------------------------------------------------------------------

/** Mirrors the run row's status as `store/append.py` writes it: a terminal
 *  type's own name, `paused` on a pause row, `running` otherwise. */
export type RunStatus = "running" | "paused" | "complete" | "failed" | "budget_exhausted";

export interface TimelineRow {
  seq: number;
  /** The row's `idempotency_key`: unique per run, the row's identity. */
  key: string;
  type: EventType;
  emitter: string;
  attempt_token: string | null;
  /** One line for the list. */
  message: string;
  /** A progress row's own status (`started`, `tool_started`, …) or the type. */
  status: string;
  agent?: string;
  tool_name?: string;
  tool_use_id?: string;
  tool_input?: unknown;
  tool_output_preview?: string;
  tool_error?: string;
  /** A completed read's run-local ordinal (`agent/hooks.py`), which a finding's
   *  anchor names. Never the row's `seq`: that is the log's, assigned on append. */
  tool_seq?: number;
  hypothesis_id?: string;
  hypothesis_status?: string;
  /** The row's payload, whole, for the types whose schema is not pinned. */
  payload: Record<string, unknown>;
}

/** The read a finding rests on (#483; `contracts/envelope.py::FindingAnchor`):
 *  the runtime's `tool_seq` and the read's call id, both off the run's ledger. */
export interface FindingAnchor {
  tool_seq: number;
  tool_use_id?: string;
  tool_name?: string;
  probe?: string;
}

export interface Finding {
  finding_id: string;
  agent: string;
  task: string;
  summary: string;
  key_facts: string[];
  severity: string;
  status: string;
  supersedes?: string;
  /** The chat turn's retraction of this finding, when one landed (#391). The
   *  finding's own fields are never rewritten by it. */
  retraction?: Retraction;
  /** Absent on a row recorded before #483: it has no recorded support, and the
   *  page says so rather than guessing one. */
  anchor?: FindingAnchor;
}

/** A `finding_retracted` row (#391, `contracts.envelope.FindingRetractedPayload`):
 *  the finding it names, what was withdrawn, what is true instead, and the row
 *  it rode on. */
export interface Retraction {
  finding_id: string;
  claim: string;
  correction: string;
  evidence: string | null;
  seq: number;
}

export interface Hypothesis {
  hypothesis_id: string;
  statement: string;
  status: string;
  evidence: string[];
  agent?: string;
  untestable?: boolean;
}

/** The `complete` row's conclusion (`orchestrator.py`, `terminal_for_result`). */
export interface Conclusion {
  summary: string | null;
  key_facts: string[];
  severity: string | null;
  recommendations: string[];
  root_cause: string | null;
  root_cause_confidence: number | null;
  /** The split conclusion: `mechanism` null beside a `mechanism_gap` is the
   *  agent's not-determined state; both null with no gap is a legacy payload. */
  mechanism: string | null;
  mechanism_gap: string | null;
  /** The runtime's own record that the alert's text contradicts the conclusion
   *  (`tools/conclusion.py`), carried as written so live and replay agree. */
  alert_contradiction: Record<string, unknown> | null;
  /** The runtime's record that a breach the conclusion rests on went unverified. */
  unverified_breach: Record<string, unknown> | null;
  trigger: string | null;
  /** Typed absence claims (B4, #298), each with its disposition and, since #523,
   *  the `demoted` marker a narrowing or a cap demotion set. A legacy string
   *  claim is kept as a statement with no disposition. */
  alternatives_considered: AlternativeClaim[] | null;
  /** `structured`, `unstructured` or `refused` (#19 Q6). A refused conclusion
   *  may still carry its mechanism when only absences were refused (#523). */
  conclusion_status: string | null;
  refusals: number | null;
}

export interface AlternativeClaim {
  statement: string;
  /** `ruled_out` or `untested`; null on a legacy string claim. */
  disposition: string | null;
  claimed_through: string | null;
  tool_seq: number | null;
  /** Why the runtime narrowed or demoted the claim: `past_horizon`,
   *  `not_windowed`, `no_anchor`, `invented_anchor`, `unparseable_claimed_through`. */
  demoted: string | null;
}

function alternatives(p: Payload): AlternativeClaim[] | undefined {
  const v = p.alternatives_considered;
  if (!Array.isArray(v)) return undefined;
  const out: AlternativeClaim[] = [];
  for (const item of v) {
    if (typeof item === "string") {
      out.push({ statement: item, disposition: null, claimed_through: null, tool_seq: null, demoted: null });
    } else if (typeof item === "object" && item !== null && !Array.isArray(item)) {
      const claim = item as Payload;
      const statement = str(claim, "statement");
      if (!statement) continue;
      const anchor = claim.anchor;
      const seq =
        typeof anchor === "object" && anchor !== null && !Array.isArray(anchor)
          ? num(anchor as Payload, "tool_seq")
          : undefined;
      out.push({
        statement,
        disposition: str(claim, "disposition") ?? null,
        claimed_through: str(claim, "claimed_through") ?? null,
        tool_seq: seq ?? null,
        demoted: str(claim, "demoted") ?? null,
      });
    }
  }
  return out;
}

/** What a reader is told when the conclusion was refused (#523), or null when it was not. */
export function refusalNotice(conclusion: Conclusion | null): string | null {
  if (!conclusion || conclusion.conclusion_status !== "refused") return null;
  const n = conclusion.refusals;
  const head = n ? `Conclusion refused after ${n} attempt${n === 1 ? "" : "s"}` : "Conclusion refused";
  if (!conclusion.mechanism) return `${head} — no mechanism was established`;
  const demoted = (conclusion.alternatives_considered ?? []).filter((c) => c.demoted);
  const untested = demoted.filter((c) => c.disposition === "untested").length;
  const narrowed = demoted.length - untested;
  const parts = [`${untested} claim${untested === 1 ? "" : "s"} untested`];
  if (narrowed) parts.push(`${narrowed} narrowed`);
  return `${head} — the mechanism stands, ${parts.join(", ")}`;
}

export interface Failure {
  reason: string | null;
  message: string | null;
}

export interface ClarificationOption {
  value: string;
  destructive: boolean;
}

export interface Clarification {
  seq: number;
  question: string;
  options: ClarificationOption[] | null;
}

/** One change's lifecycle, from the platform's `action_*` rows: the phase is
 *  the last row's, the payload is that row's, whole (#6). */
export interface ActionState {
  change_id: string;
  phase: string;
  seq: number;
  payload: Record<string, unknown>;
}

/** One consent request, from `request_consent` (runtime) and
 *  `consent_resolved` (platform, `consent:{request_id}:{phase}`). */
export interface ConsentState {
  request_id: string;
  phase: string;
  seq: number;
  payload: Record<string, unknown>;
}

/** The run's `context` row: the plan the model opened with, and the run's
 *  memory declaration (#31 item 6). Stored since #399. */
export interface WorkingContext {
  plan: unknown;
  current_step: number;
  time_window?: unknown;
  memory?: unknown;
}

/** The pushed rows' fold: live only, never replayed, read by nothing durable. */
export interface EphemeralState {
  reasoning: string;
}

export interface RunProjection {
  runId: string | null;
  /** The highest seq folded: the resume cursor's twin, and the idempotence guard. */
  seq: number;
  status: RunStatus | null;
  /** The attempt the latest runtime row was written under, and the latest
   *  checkpoint a row confirmed (`resumed` names the one it rehydrated from,
   *  #16 item 7; a batch's exit row names the one it confirmed). */
  attempt: { token: string; checkpoint_seq: number | null } | null;
  timeline: TimelineRow[];
  findings: Finding[];
  hypotheses: Hypothesis[];
  conclusion: Conclusion | null;
  failure: Failure | null;
  clarification: Clarification | null;
  /** The `request_action` row the run is paused on, whole. */
  requestedAction: Record<string, unknown> | null;
  actions: ActionState[];
  consents: ConsentState[];
  outcome: Record<string, unknown> | null;
  /** The latest `context` row; a later segment's replaces an earlier one's. */
  context: WorkingContext | null;
  /** The first `started` progress row's append time, as the record's `started_at` (#1374). */
  started_at: string | null;
  /** The run's own terminal's append time, never a chat turn's (#1374). */
  finished_at: string | null;
  ephemeral: EphemeralState;
}

export function createInitialProjectionState(): RunProjection {
  return {
    runId: null,
    seq: 0,
    status: null,
    attempt: null,
    timeline: [],
    findings: [],
    hypotheses: [],
    conclusion: null,
    failure: null,
    clarification: null,
    requestedAction: null,
    actions: [],
    consents: [],
    outcome: null,
    context: null,
    started_at: null,
    finished_at: null,
    ephemeral: { reasoning: "" },
  };
}

/** The durable projection alone: what the log determines, and what a replay
 *  must reproduce. The properties test compares this half. */
export function durable(state: RunProjection): Omit<RunProjection, "ephemeral"> {
  const { ephemeral: _ephemeral, ...rest } = state;
  return rest;
}

// ---------------------------------------------------------------------------
// The status vocabulary, the store's
// ---------------------------------------------------------------------------

const TERMINAL_TYPES: ReadonlySet<string> = new Set(["complete", "failed", "budget_exhausted"]);
const PAUSE_TYPES: ReadonlySet<string> = new Set(["request_action", "request_consent"]);

const PLATFORM_TYPES: ReadonlySet<string> = new Set([
  "consent_resolved",
  "action_eligibility",
  "action_approved",
  "action_declined",
  "action_applied",
  "action_verified",
  "action_failed",
  "action_expired",
  "outcome_observed",
]);

export function isTerminal(status: RunStatus | null): boolean {
  return status !== null && TERMINAL_TYPES.has(status);
}

// ---------------------------------------------------------------------------
// Payload readers: defensive, because the schema per type is not pinned
// ---------------------------------------------------------------------------

type Payload = Record<string, unknown>;

function str(p: Payload, key: string): string | undefined {
  const v = p[key];
  return typeof v === "string" ? v : undefined;
}

function record(p: Payload, key: string): Record<string, unknown> | undefined {
  const v = p[key];
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
}

function num(p: Payload, key: string): number | undefined {
  const v = p[key];
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

function strings(p: Payload, key: string): string[] | undefined {
  const v = p[key];
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : undefined;
}

function objects(p: Payload, key: string): Payload[] | undefined {
  const v = p[key];
  return Array.isArray(v)
    ? v.filter((x): x is Payload => typeof x === "object" && x !== null && !Array.isArray(x))
    : undefined;
}

/** `{prefix}:{id}:{phase}`, the platform rows' key shape (`envelope.py`). */
function platformKey(key: string): { id: string; phase: string } | null {
  const parts = key.split(":");
  return parts.length === 3 && parts[1] && parts[2] ? { id: parts[1], phase: parts[2] } : null;
}

// ---------------------------------------------------------------------------
// Merges: defined fields win, absent ones never blank an existing value
// ---------------------------------------------------------------------------

/** Defined-wins. A later row that omits a field cannot erase what an earlier
 *  one supplied (#1502): the conclude-time `findings` delta is thinner than
 *  the `finding` rows, and it arrives second. */
function mergeDefined<T extends object>(existing: T, incoming: Partial<T>): T {
  const merged: Record<string, unknown> = { ...(existing as Record<string, unknown>) };
  for (const [k, v] of Object.entries(incoming)) {
    if (v !== undefined) merged[k] = v;
  }
  return merged as T;
}

/** Substantive-wins: `evidence: []` is *defined*, and would still erase a
 *  populated list under defined-wins (#1510). */
function mergeSubstantive<T extends object>(existing: T, incoming: Partial<T>): T {
  const merged: Record<string, unknown> = { ...(existing as Record<string, unknown>) };
  for (const [k, v] of Object.entries(incoming)) {
    const substantive =
      v !== undefined &&
      v !== null &&
      !(Array.isArray(v) && v.length === 0) &&
      !(typeof v === "string" && v.length === 0);
    if (substantive) merged[k] = v;
  }
  return merged as T;
}

/** The finding a payload carries: only the fields it names. A new finding
 *  takes defaults for the rest; a known one keeps what it had. */
function readFinding(p: Payload): Partial<Finding> & { finding_id: string } {
  return {
    finding_id: str(p, "finding_id") ?? "",
    agent: str(p, "agent"),
    task: str(p, "task"),
    summary: str(p, "summary"),
    key_facts: strings(p, "key_facts"),
    severity: str(p, "severity"),
    status: str(p, "status"),
    supersedes: str(p, "supersedes"),
    anchor: anchorOf(p.anchor),
  };
}

/** The anchor a payload carries, or `undefined` when it carries none that names a
 *  read: a `tool_seq` that is not a positive integer is not an anchor. */
function anchorOf(value: unknown): FindingAnchor | undefined {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return undefined;
  const p = value as Payload;
  const tool_seq = num(p, "tool_seq");
  if (tool_seq === undefined || !Number.isInteger(tool_seq) || tool_seq < 1) return undefined;
  return {
    tool_seq,
    tool_use_id: str(p, "tool_use_id"),
    tool_name: str(p, "tool_name"),
    probe: str(p, "probe"),
  };
}

const EMPTY_FINDING: Finding = {
  finding_id: "",
  agent: "",
  task: "",
  summary: "",
  key_facts: [],
  severity: "",
  status: "",
};

function upsertFinding(findings: Finding[], incoming: Partial<Finding> & { finding_id: string }): Finding[] {
  const idx = incoming.finding_id
    ? findings.findIndex((f) => f.finding_id === incoming.finding_id)
    : -1;
  if (idx < 0) return [...findings, mergeDefined(EMPTY_FINDING, incoming)];
  const next = [...findings];
  next[idx] = mergeDefined(findings[idx], incoming);
  return next;
}

/** Neither the `finding` rows nor the conclude list is authoritative about
 *  membership (#1509): the list is a delta, so every id is kept and every id
 *  is merged, and nothing here deletes. */
function mergeFindings(existing: Finding[], incoming: Payload[]): Finding[] {
  return incoming
    .map(readFinding)
    .filter((f) => f.finding_id || f.summary)
    .reduce(upsertFinding, existing);
}

/** A retraction row's payload, or null when it marks nothing: the contract
 *  refuses a row with no `finding_id`, claim or correction, and a row that
 *  arrives without one anyway is not a marker to put over a claim. */
function readRetraction(p: Payload, seq: number): Retraction | null {
  const finding_id = str(p, "finding_id");
  const claim = str(p, "claim");
  const correction = str(p, "correction");
  if (!finding_id || !claim || !correction) return null;
  return {
    finding_id,
    claim,
    correction,
    evidence: str(p, "evidence") ?? null,
    seq,
  };
}

function readHypothesis(p: Payload): Partial<Hypothesis> & { hypothesis_id: string; status: string } {
  return {
    hypothesis_id: str(p, "hypothesis_id") ?? "",
    statement: str(p, "statement"),
    status: str(p, "status") ?? "",
    evidence: strings(p, "evidence"),
    agent: str(p, "agent"),
    untestable: p.untestable === true ? true : undefined,
  };
}

const EMPTY_HYPOTHESIS: Hypothesis = { hypothesis_id: "", statement: "", status: "", evidence: [] };

function upsertHypothesis(hypotheses: Hypothesis[], incoming: Partial<Hypothesis> & { hypothesis_id: string }): Hypothesis[] {
  const idx = hypotheses.findIndex((h) => h.hypothesis_id === incoming.hypothesis_id);
  if (idx < 0) return [...hypotheses, mergeDefined(EMPTY_HYPOTHESIS, incoming)];
  const next = [...hypotheses];
  next[idx] = mergeSubstantive(hypotheses[idx], incoming);
  return next;
}

// ---------------------------------------------------------------------------
// The timeline
// ---------------------------------------------------------------------------

const TOOL_OUTCOMES: ReadonlySet<string> = new Set(["tool_completed", "tool_failed"]);

function baseRow(row: DurableRow, message: string, status?: string): TimelineRow {
  return {
    seq: row.seq,
    key: row.idempotency_key,
    type: row.type,
    emitter: row.emitter,
    attempt_token: row.attempt_token,
    message,
    status: status ?? row.type,
    payload: row.payload,
  };
}

/** Append a row, or — for a tool outcome — merge it into the row that started
 *  the same call, so the start's `tool_input` survives the completion. The
 *  merged row takes the outcome's seq and key: it is the later fact. */
function addTimelineRow(timeline: TimelineRow[], row: TimelineRow): TimelineRow[] {
  if (TOOL_OUTCOMES.has(row.status)) {
    let idx = -1;
    if (row.tool_use_id) {
      idx = timeline.findIndex(
        (e) => e.tool_use_id === row.tool_use_id && e.status === "tool_started",
      );
    }
    if (idx < 0 && row.tool_name) {
      for (let i = timeline.length - 1; i >= 0; i--) {
        if (timeline[i].tool_name === row.tool_name && timeline[i].status === "tool_started") {
          idx = i;
          break;
        }
      }
    }
    if (idx >= 0) {
      const next = [...timeline];
      next[idx] = mergeDefined(timeline[idx], row);
      return next;
    }
  }
  return [...timeline, row];
}

/** A tool-lifecycle row, from either spelling: the `progress` row the runtime
 *  records with `status: tool_started|tool_completed|tool_failed`
 *  (`hooks.py`), or the envelope's own `tool_started` / `tool_completed`
 *  types. One shape, so the two cannot render differently. */
function toolRow(row: DurableRow, status: string): TimelineRow {
  const p = row.payload;
  const tool_name = str(p, "tool_name") ?? "";
  return {
    ...baseRow(row, str(p, "message") ?? `${status}: ${tool_name}`, status),
    agent: str(p, "agent"),
    tool_name,
    tool_use_id: str(p, "tool_use_id"),
    tool_input: p.tool_input,
    tool_output_preview: str(p, "tool_output_preview"),
    tool_error: str(p, "tool_error"),
    tool_seq: num(p, "tool_seq"),
  };
}

// ---------------------------------------------------------------------------
// A finding's evidence: the one read its anchor names
// ---------------------------------------------------------------------------

/** What a finding's evidence link can open (#483). `read` is the completed tool
 *  row its anchor names; `unanchored` is a finding recorded with no anchor;
 *  `missing` is an anchor no row of this projection answers — never a guess. */
export type FindingEvidence =
  | { kind: "read"; anchor: FindingAnchor; row: TimelineRow }
  | { kind: "unanchored" }
  | { kind: "missing"; anchor: FindingAnchor };

/** The read a finding's anchor names, joined on **both** fields it carries: the
 *  run-local `tool_seq` and the read's `tool_use_id`. A number alone is not an
 *  identity across a run's segments — a chat turn numbers its reads from its own
 *  scope — and a join that matched two rows would be a guess, so it links none.
 *  A function of the projection alone, so the live tab and a replay agree. */
export function findingEvidence(projection: RunProjection, finding: Finding): FindingEvidence {
  const anchor = finding.anchor;
  if (!anchor) return { kind: "unanchored" };
  if (!anchor.tool_use_id) return { kind: "missing", anchor };
  const rows = projection.timeline.filter(
    (row) =>
      row.status === "tool_completed" &&
      row.tool_seq === anchor.tool_seq &&
      row.tool_use_id === anchor.tool_use_id,
  );
  return rows.length === 1 ? { kind: "read", anchor, row: rows[0] } : { kind: "missing", anchor };
}

// ---------------------------------------------------------------------------
// applyEvent — the one fold, live and replay
// ---------------------------------------------------------------------------

/**
 * Fold one row. Pure: returns the next projection, never mutates.
 *
 * A durable row at or below `state.seq` has been folded already — a resume's
 * overlap, a duplicate — and is dropped, so folding a row twice is folding it
 * once. An ephemeral row has no seq and touches `state.ephemeral` only.
 */
export function applyEvent(state: RunProjection, row: LogRow): RunProjection {
  if (row.ephemeral) return applyEphemeral(state, row as EphemeralRow);
  if (row.seq == null) {
    throw new Error(`a durable ${row.type} row carries the server's sequence number; this one has none`);
  }
  if (row.seq <= state.seq) return state;
  return applyDurable(
    applyClocks({ ...state, seq: row.seq, runId: state.runId ?? row.run_id }, row as DurableRow),
    row as DurableRow,
  );
}

/** The run's Started and Finished, off the rows' append times (#1374): the first
 *  `started` progress row, and the run's own first terminal. Each is set once. */
function applyClocks(state: RunProjection, row: DurableRow): RunProjection {
  const at = row.appended_at ?? null;
  if (at === null) return state;
  if (state.started_at === null && row.type === "progress" && row.payload.status === "started")
    return { ...state, started_at: at };
  if (state.finished_at === null && TERMINAL_TYPES.has(row.type) && !row.attempt_token?.startsWith("chat:"))
    return { ...state, finished_at: at };
  return state;
}

/** Fold rows in order: the replay, a batch, a generated log. */
export function fold(rows: Iterable<LogRow>, state = createInitialProjectionState()): RunProjection {
  let next = state;
  for (const row of rows) next = applyEvent(next, row);
  return next;
}

function applyEphemeral(state: RunProjection, row: EphemeralRow): RunProjection {
  const p = row.payload;
  switch (row.type) {
    case "stream":
      return {
        ...state,
        ephemeral: {
          ...state.ephemeral,
          reasoning: appendCapped(state.ephemeral.reasoning, str(p, "content") ?? ""),
        },
      };
    default:
      // The envelope marks exactly one type ephemeral, `stream` (`envelope.py`,
      // `EPHEMERAL_TYPES`; `context` left it with #399); any other is a row the
      // server would have refused.
      return state;
  }
}

function readContext(p: Payload): WorkingContext {
  return {
    plan: p.plan,
    current_step: num(p, "current_step") ?? Number(p.current_step ?? 0),
    time_window: p.time_window,
    memory: p.memory,
  };
}

function applyStatus(state: RunProjection, row: DurableRow): RunProjection {
  // The platform's rows describe a change or a consent, never the run's
  // status; and a terminal status is left alone by every later row — the
  // log outlives `complete`, the status does not move past it (#6). A chat
  // segment has its own lifecycle; its rows only advance the shared attempt
  // cursor here, never the original investigation status.
  if (PLATFORM_TYPES.has(row.type)) return state;
  let status: RunStatus | null = state.status ?? "running";
  if (row.attempt_token?.startsWith("chat:")) status = state.status;
  else if (TERMINAL_TYPES.has(row.type)) status = row.type as RunStatus;
  else if (PAUSE_TYPES.has(row.type)) status = isTerminal(status) ? status : "paused";
  else if (row.type === "resumed" && status === "paused") status = "running";
  // The token is the latest runtime row's; the checkpoint seq moves only
  // when a row names one, so it is the latest confirmed checkpoint.
  const attempt =
    row.attempt_token === null
      ? state.attempt
      : {
          token: row.attempt_token,
          checkpoint_seq: row.checkpoint_seq ?? state.attempt?.checkpoint_seq ?? null,
        };
  return { ...state, status, attempt };
}

function applyDurable(state: RunProjection, row: DurableRow): RunProjection {
  const next = applyStatus(state, row);
  const p = row.payload;
  // A chat terminal answers its question; it never revises the investigation's
  // result. Durable findings remain shared evidence across segments.
  if (
    row.attempt_token?.startsWith("chat:") &&
    (TERMINAL_TYPES.has(row.type) || row.type === "clarification")
  ) {
    return {
      ...next,
      findings:
        row.type === "complete"
          ? mergeFindings(next.findings, objects(p, "findings") ?? [])
          : next.findings,
      timeline: addTimelineRow(
        next.timeline,
        baseRow(row, str(p, "answer") ?? str(p, "message") ?? str(p, "question") ?? row.type),
      ),
    };
  }
  switch (row.type) {
    case "progress": {
      const status = str(p, "status") ?? "progress";
      if (status.startsWith("tool_")) {
        return { ...next, timeline: addTimelineRow(next.timeline, toolRow(row, status)) };
      }
      return {
        ...next,
        timeline: addTimelineRow(next.timeline, {
          ...baseRow(row, str(p, "message") ?? status, status),
          agent: str(p, "agent"),
        }),
      };
    }

    case "tool_started":
    case "tool_completed": {
      // `tool_completed` carries `tool_error` when the call failed; the row's
      // own status says so, so a failed completion still merges into its start.
      const status = str(p, "status") ?? row.type;
      return { ...next, timeline: addTimelineRow(next.timeline, toolRow(row, status)) };
    }

    case "finding": {
      const finding = readFinding(p);
      return {
        ...next,
        findings: upsertFinding(next.findings, finding),
        timeline: addTimelineRow(next.timeline, {
          ...baseRow(row, finding.summary ?? ""),
          agent: finding.agent,
        }),
      };
    }

    case "hypothesis": {
      const hypothesis = readHypothesis(p);
      // Announced once per (hypothesis, status) for the whole run, however
      // many rows restate it: a theory the agent cannot test is revised at
      // the SAME status to name the instrument it lacks (ac#523), and its
      // evidence moves while the timeline does not stutter. A status it held
      // before and returns to is no announcement either — investigating,
      // refuted, investigating is three rows and two announcements, the rule
      // the old estate's sentinel wrote and the ported tests pin.
      const restated = next.timeline.some(
        (e) =>
          e.hypothesis_id === hypothesis.hypothesis_id &&
          e.hypothesis_status === hypothesis.status,
      );
      return {
        ...next,
        hypotheses: upsertHypothesis(next.hypotheses, hypothesis),
        timeline: restated
          ? next.timeline
          : addTimelineRow(next.timeline, {
              ...baseRow(row, `${hypothesis.status}: ${hypothesis.statement ?? ""}`),
              agent: hypothesis.agent,
              hypothesis_id: hypothesis.hypothesis_id,
              hypothesis_status: hypothesis.status,
            }),
      };
    }

    case "complete": {
      const incoming: Partial<Conclusion> = {
        summary: str(p, "summary"),
        key_facts: strings(p, "key_facts"),
        severity: str(p, "severity"),
        recommendations: strings(p, "recommendations"),
        root_cause: str(p, "root_cause"),
        root_cause_confidence: num(p, "root_cause_confidence"),
        // Presence, not truth: an undetermined mechanism is an explicit null
        // beside a gap, and the pair is what marks the payload as split.
        ...("mechanism" in p || "mechanism_gap" in p
          ? { mechanism: str(p, "mechanism") ?? null, mechanism_gap: str(p, "mechanism_gap") ?? null }
          : {}),
        ...("alert_contradiction" in p ? { alert_contradiction: record(p, "alert_contradiction") ?? null } : {}),
        ...("unverified_breach" in p ? { unverified_breach: record(p, "unverified_breach") ?? null } : {}),
        ...("trigger" in p ? { trigger: str(p, "trigger") ?? null } : {}),
        ...("alternatives_considered" in p
          ? { alternatives_considered: alternatives(p) ?? null }
          : {}),
        conclusion_status: str(p, "conclusion_status"),
        refusals: num(p, "refusals"),
      };
      const empty: Conclusion = {
        summary: null,
        key_facts: [],
        severity: null,
        recommendations: [],
        root_cause: null,
        root_cause_confidence: null,
        mechanism: null,
        mechanism_gap: null,
        alert_contradiction: null,
        unverified_breach: null,
        trigger: null,
        alternatives_considered: null,
        conclusion_status: null,
        refusals: null,
      };
      return {
        ...next,
        conclusion: mergeDefined(next.conclusion ?? empty, incoming),
        findings: mergeFindings(next.findings, objects(p, "findings") ?? []),
        timeline: addTimelineRow(next.timeline, baseRow(row, str(p, "summary") ?? "complete")),
      };
    }

    case "failed": {
      const failure: Failure = {
        reason: str(p, "failure_reason") ?? null,
        message: str(p, "message") ?? null,
      };
      return {
        ...next,
        failure,
        timeline: addTimelineRow(next.timeline, baseRow(row, failure.message ?? "failed")),
      };
    }

    case "budget_exhausted":
      return {
        ...next,
        timeline: addTimelineRow(next.timeline, baseRow(row, str(p, "message") ?? "budget exhausted")),
      };

    case "clarification": {
      const question = str(p, "question") ?? "";
      return {
        ...next,
        clarification: question
          ? { seq: row.seq, question, options: clarificationOptions(p.options) }
          : next.clarification,
        timeline: addTimelineRow(next.timeline, baseRow(row, question || "clarification")),
      };
    }

    case "request_action":
      return {
        ...next,
        requestedAction: p,
        timeline: addTimelineRow(next.timeline, baseRow(row, str(p, "message") ?? "action requested")),
      };

    case "request_consent": {
      const request_id = str(p, "request_id") ?? str(p, "consent_id") ?? row.idempotency_key;
      return {
        ...next,
        consents: upsertByKey(next.consents, "request_id", {
          request_id,
          phase: "requested",
          seq: row.seq,
          payload: p,
        }),
        timeline: addTimelineRow(next.timeline, baseRow(row, str(p, "message") ?? "consent requested")),
      };
    }

    case "consent_resolved": {
      const key = platformKey(row.idempotency_key);
      return {
        ...next,
        consents: upsertByKey(next.consents, "request_id", {
          request_id: key?.id ?? row.idempotency_key,
          phase: key?.phase ?? row.type,
          seq: row.seq,
          payload: p,
        }),
        timeline: addTimelineRow(next.timeline, baseRow(row, str(p, "message") ?? row.type)),
      };
    }

    case "action_eligibility":
    case "action_approved":
    case "action_declined":
    case "action_applied":
    case "action_verified":
    case "action_failed":
    case "action_expired": {
      const key = platformKey(row.idempotency_key);
      return {
        ...next,
        actions: upsertByKey(next.actions, "change_id", {
          change_id: key?.id ?? row.idempotency_key,
          phase: key?.phase ?? row.type.replace("action_", ""),
          seq: row.seq,
          payload: p,
        }),
        timeline: addTimelineRow(next.timeline, baseRow(row, str(p, "message") ?? row.type)),
      };
    }

    case "outcome_observed":
      return {
        ...next,
        outcome: p,
        timeline: addTimelineRow(next.timeline, baseRow(row, str(p, "message") ?? row.type)),
      };

    case "finding_retracted": {
      // #391. The rule is `contracts.envelope.retractions_of`'s, held here so
      // the page and the record route agree on which findings stand: a pointer
      // resolves against a finding this fold already holds — which is this
      // run's log, in seq order — or is dropped; a row naming no finding marks
      // nothing; a later retraction of one finding replaces an earlier one. The
      // finding is never rewritten.
      const retraction = readRetraction(p, row.seq);
      const timeline = addTimelineRow(next.timeline, baseRow(row, `retracted: ${retraction?.claim ?? ""}`));
      if (retraction === null) return { ...next, timeline };
      const idx = next.findings.findIndex((f) => f.finding_id === retraction.finding_id);
      if (idx < 0) return { ...next, timeline };
      const findings = [...next.findings];
      findings[idx] = { ...findings[idx], retraction };
      return { ...next, findings, timeline };
    }

    case "tool_refused":
    case "resumed":
      return {
        ...next,
        timeline: addTimelineRow(next.timeline, {
          ...baseRow(row, str(p, "message") ?? describe(row)),
          tool_name: str(p, "tool_name"),
        }),
      };

    case "context":
      // Stored since #399: the plan the model opened with and the run's memory
      // declaration, so a reload and a replay both have them. A resumed or
      // redelivered segment's own `context` replaces the one before it.
      return {
        ...next,
        context: readContext(p),
        timeline: addTimelineRow(next.timeline, baseRow(row, str(p, "plan") ?? row.type)),
      };

    case "stream":
      // Ephemeral by the envelope's own rule; a durable one is a server
      // defect. Folded as a bare timeline row rather than dropped, so it is
      // seen.
      return { ...next, timeline: addTimelineRow(next.timeline, baseRow(row, row.type)) };

    default:
      return assertEveryTypeFolded(row.type, next);
  }
}

/** Compile-time proof that the switch covers every type the envelope names:
 *  a type added to `Event.type` makes `type` here not `never`. */
function assertEveryTypeFolded(type: never, state: RunProjection): RunProjection {
  void type;
  return state;
}

function describe(row: DurableRow): string {
  if (row.type === "resumed") {
    const cause = str(row.payload, "cause");
    return cause ? `resumed (${cause})` : "resumed";
  }
  return row.type;
}

function upsertByKey<T extends Record<K, string>, K extends keyof T>(
  list: T[],
  key: K,
  incoming: T,
): T[] {
  const idx = list.findIndex((x) => x[key] === incoming[key]);
  if (idx < 0) return [...list, incoming];
  const next = [...list];
  next[idx] = incoming;
  return next;
}

function clarificationOptions(raw: unknown): ClarificationOption[] | null {
  if (!Array.isArray(raw)) return null;
  const options: ClarificationOption[] = [];
  for (const opt of raw) {
    if (typeof opt === "string") options.push({ value: opt, destructive: false });
    else if (typeof opt === "object" && opt !== null && typeof (opt as Payload).value === "string") {
      options.push({
        value: (opt as Payload).value as string,
        destructive: (opt as Payload).destructive === true,
      });
    }
  }
  return options;
}

// ---------------------------------------------------------------------------
// The reasoning stream's cap
// ---------------------------------------------------------------------------

/** The live reasoning text kept in memory; older text is trimmed from the
 *  front, aligned to a word boundary when one is near. Pushed, never stored,
 *  so a cap here costs nothing durable. */
export const REASONING_STREAM_CAP_CHARS = 5000;

function appendCapped(existing: string, text: string): string {
  const next = existing + text;
  if (next.length <= REASONING_STREAM_CAP_CHARS) return next;
  const trimmed = next.slice(next.length - REASONING_STREAM_CAP_CHARS);
  const ws = trimmed.search(/\s/);
  return ws > 0 && ws < 50 ? trimmed.slice(ws + 1) : trimmed;
}
